import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { isEmpty, isNil, uniqBy } from 'lodash'
import queryString from 'query-string'
import { IconMessageCircle } from '@tabler/icons-react'

import { Pill } from '@/components/pills/Pill'
import { FilterTypeSelect } from '@/components/filters/types/FilterTypeSelect'
import { focusChangeRequest } from '@/reducers/voiceAgents/voiceAgents.redux'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'
import { locateSnippet } from '@/components/comments/anchorSnippet'

import {
  buildTranscriptCommentCallLink,
  DRAWER_TAB_BY_TARGET_TYPE,
  getUiStatus,
  isOpenRequest,
  UI_STATUS,
} from '../changeRequestHelpers'
import { composeSegmentsText } from '../promptOffsets'
import ChangeRequestCard from './ChangeRequestCard'
import EmptyState from './EmptyState'

const EMPTY_COPY = {
  [UI_STATUS.OPEN]: {
    title: 'No open change requests',
    hint: 'Comments are created from the Call Flow drawer — open a node and add a comment from its Comments view.',
  },
  [UI_STATUS.RESOLVED]: {
    title: 'No resolved change requests',
    hint: 'Resolved and rejected comments from the Call Flow drawer show up here.',
  },
}

// Top-level Change Requests tab (?tab=change-requests): every comment on the
// agent in one aggregated list, filterable by UI status and comment author.
// Clicking a navigable card jumps back into the Call Flow tab with the target
// node selected and the request focused in the drawer's Comments view.
const ChangeRequestsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const items = useSelector((state) => state.voiceAgents.changeRequests.items)
  const loading = useSelector((state) => state.voiceAgents.loading.changeRequests)
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))

  const [statusFilter, setStatusFilter] = useState(UI_STATUS.OPEN)
  const [authorFilter, setAuthorFilter] = useState(null)

  const nodes = Array.isArray(agent?.nodes) ? agent.nodes : []
  // Filter by the stable user id but display the resolved name (names can
  // collide; ids can't). Name falls back to the id for rows without one.
  const authorOptions = uniqBy(items, 'createdBy').map((request) => ({
    value: request.createdBy,
    label: request.createdByName || request.createdBy,
  }))
  const openCount = items.filter(isOpenRequest).length
  // Every request maps to exactly one of the two UI statuses, so the
  // complement is the resolved count.
  const resolvedCount = items.length - openCount

  const filteredItems = items.filter(
    (request) =>
      getUiStatus(request) === statusFilter &&
      (isNil(authorFilter) || request.createdBy === authorFilter)
  )

  // Only OPEN prompt-target requests navigate to the Call Flow drawer — the
  // drawer lists open comments only, so a resolved card would land on a list
  // that doesn't contain it. Agent-level requests (target id null) are
  // likewise inert.
  const isNavigable = (request) =>
    isOpenRequest(request) &&
    !isNil(request.target?.id) &&
    !isNil(DRAWER_TAB_BY_TARGET_TYPE[request.target?.type])

  // Transcript comments live on a call, not a node — they deep-link out to the
  // Call Explorer instead of the in-page Call Flow drawer. Open or resolved both
  // navigate (the Call Explorer reveals either).
  const isTranscriptNavigable = (request) =>
    request.type === 'transcript' && !isNil(request.call?.id)

  const findNodeTitle = (request) =>
    nodes.find((node) => node.id === request.target?.id)?.title || request.target?.id

  // Orphaned = the highlight's snippet is gone from its node's current prompt
  // text. Re-anchor against that node (active version) to flag the card.
  const isOrphaned = (request) => {
    // A transcript comment quotes speech, not prompt text — it is never
    // orphaned against the prompt it was matched to (AVR-878).
    if (!request.highlight?.snippet || request.type === 'transcript') {
      return false
    }
    const node = nodes.find((candidate) => candidate.id === request.target?.id)
    if (isNil(node?.config)) {
      return false
    }
    const { text } = composeSegmentsText(node.config.segments, node.config.promptText)
    return isNil(locateSnippet(text, request.highlight.snippet, request.highlight.start))
  }

  const handleCardClick = (request) => {
    dispatch(
      focusChangeRequest({
        nodeId: request.target.id,
        tabKey: DRAWER_TAB_BY_TARGET_TYPE[request.target.type] || 'prompt',
        requestId: request.id,
      })
    )
    history.replace({
      pathname: location.pathname,
      search: queryString.stringify({ tab: 'call-flow' }),
    })
  }

  // A transcript comment that knows its node opens the node — that is where the
  // prompt engineer fixes it (AVR-878). The call deep link stays as the
  // fallback for the ones with no node, and for resolved ones, which the
  // drawer's open-only list would not show.
  const resolveCardClick = (request) => {
    if (isNavigable(request)) {
      return () => handleCardClick(request)
    }
    if (isTranscriptNavigable(request)) {
      return () => history.push(buildTranscriptCommentCallLink(request))
    }
    return undefined
  }

  const isInitialLoading = loading && isEmpty(items)

  return (
    <div
      className="voice-agents__change-requests-page voice-agents__cr-page"
      data-testid="change-requests-page"
    >
      <div className="voice-agents__cr-page-filters">
        <Pill
          clickable
          outlined
          selected={statusFilter === UI_STATUS.OPEN}
          onClick={() => setStatusFilter(UI_STATUS.OPEN)}
          dataTestId="change-requests-filter-open"
        >
          Open ({openCount})
        </Pill>
        <Pill
          clickable
          outlined
          selected={statusFilter === UI_STATUS.RESOLVED}
          onClick={() => setStatusFilter(UI_STATUS.RESOLVED)}
          dataTestId="change-requests-filter-resolved"
        >
          Resolved ({resolvedCount})
        </Pill>
        <span className="voice-agents__cr-page-divider" aria-hidden="true" />
        <FilterTypeSelect
          autoFocus={false}
          showAllOnEmpty
          showAllOnEmptyLabel="All"
          schema={{ accessor: 'author', label: 'Comment by', options: authorOptions }}
          value={authorFilter ?? ''}
          updateSearch={(accessor, value) => setAuthorFilter(value || null)}
          dataTestId="change-requests-author-filter"
        />
      </div>
      {!isInitialLoading && isEmpty(filteredItems) && (
        <EmptyState
          icon={IconMessageCircle}
          title={EMPTY_COPY[statusFilter].title}
          hint={EMPTY_COPY[statusFilter].hint}
          dataTestId="change-requests-page-empty"
        />
      )}
      {!isEmpty(filteredItems) && (
        <div className="voice-agents__cr-page-list" data-testid="change-requests-page-list">
          {filteredItems.map((request) => (
            <ChangeRequestCard
              key={request.id}
              request={request}
              nodeTitle={isNil(request.target?.id) ? undefined : findNodeTitle(request)}
              orphaned={isOrphaned(request)}
              showCallLink
              onClick={resolveCardClick(request)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default ChangeRequestsPage
